import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Loader2, Plus, Activity, FileText, Link as LinkIcon, Paperclip, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow, parseISO } from "date-fns";

const STATUS_META = {
  on_track: { label: "On track", style: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  at_risk: { label: "At risk", style: "bg-amber-50 text-amber-700 border-amber-200" },
  blocked: { label: "Blocked", style: "bg-rose-50 text-rose-700 border-rose-200" },
  complete: { label: "Complete", style: "bg-slate-100 text-slate-800 border-slate-300" },
};

export default function CheckIns() {
  const { user } = useAuth();
  const [activeCycles, setActiveCycles] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openObjId, setOpenObjId] = useState(null);

  const cycle = activeCycles[0]?.cycle;

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cycles/active/me");
      setActiveCycles(r.data.items || []);
      if (r.data.items?.[0]) {
        const cid = r.data.items[0].cycle.id;
        const [o, c] = await Promise.all([
          api.get("/objectives", { params: { cycle_id: cid } }),
          api.get("/check-ins", { params: { cycle_id: cid } }),
        ]);
        setObjectives(o.data.items || []);
        setCheckIns(c.data.items || []);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  if (!user?.employee_id) {
    return <div className="axia-card p-8 text-sm text-slate-500">Your account isn't linked to an employee record yet.</div>;
  }
  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  if (!cycle) {
    return (
      <div className="max-w-2xl axia-card p-12 text-center" data-testid="checkins-no-cycle">
        <Activity className="w-7 h-7 mx-auto text-slate-300" />
        <h1 className="font-display text-2xl font-bold text-slate-900 mt-3">No active cycle</h1>
        <p className="text-sm text-slate-500 mt-2">When HR activates a cycle, you'll log check-ins here.</p>
      </div>
    );
  }

  // Group check-ins by objective
  const checkInsByObj = checkIns.reduce((acc, c) => {
    (acc[c.objective_id] = acc[c.objective_id] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="max-w-6xl space-y-6" data-testid="checkins-page">
      <div>
        <div className="axia-overline">Continuous performance</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Check-ins</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">
          A weekly habit. Log progress per objective, flag blockers early, give your manager a real-time picture without scheduling a meeting.
        </p>
      </div>

      {objectives.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500">
          <p className="text-sm">Set goals first — once your objectives are approved, check in against them here.</p>
          <Link to="/goals" className="mt-3 inline-flex h-9 px-3 items-center bg-slate-900 text-white text-sm rounded-md hover:bg-slate-800">Go to My Goals</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {objectives.map((o) => {
            const history = (checkInsByObj[o.id] || []).slice(0, 5);
            const last = history[0];
            const meta = STATUS_META[last?.status] || STATUS_META.on_track;
            return (
              <div key={o.id} className="axia-card p-5" data-testid={`checkin-obj-${o.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`axia-pill ${meta.style}`}>{last ? meta.label : "no check-ins yet"}</span>
                      <span className="axia-overline">weight {o.weightage}%</span>
                      {o.status === "locked" && <span className="axia-pill bg-emerald-50 text-emerald-700 border-emerald-200">locked</span>}
                    </div>
                    <div className="mt-2.5 font-display text-base font-semibold text-slate-900 leading-snug">
                      {o.fields?.statement || o.fields?.objective || "Objective"}
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500">Last reported progress</span>
                        <span className="font-mono text-slate-700">{last?.progress_pct ?? 0}%</span>
                      </div>
                      <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-slate-900" style={{ width: `${last?.progress_pct ?? 0}%` }} />
                      </div>
                    </div>
                  </div>
                  <Dialog open={openObjId === o.id} onOpenChange={(v) => setOpenObjId(v ? o.id : null)}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid={`add-checkin-${o.id}`}><Plus className="w-3.5 h-3.5 mr-1.5" /> Check in</Button>
                    </DialogTrigger>
                    <CheckInDialog
                      objective={o}
                      cycleId={cycle.id}
                      lastProgress={last?.progress_pct ?? 0}
                      onClose={() => { setOpenObjId(null); reload(); }}
                    />
                  </Dialog>
                </div>

                {history.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-slate-100">
                    <div className="axia-overline mb-2">Recent updates</div>
                    <ul className="space-y-2">
                      {history.map((c) => {
                        const m = STATUS_META[c.status] || STATUS_META.on_track;
                        let ago = "";
                        try { ago = formatDistanceToNow(parseISO(c.created_at), { addSuffix: true }); } catch {}
                        return (
                          <li key={c.id} className="text-sm flex items-start gap-3">
                            <span className={`axia-pill shrink-0 ${m.style}`}>{c.progress_pct}% · {m.label}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-slate-700 whitespace-pre-wrap">{c.commentary || <span className="text-slate-400">No commentary</span>}</div>
                              {c.blockers && (
                                <div className="text-xs text-rose-700 mt-1 inline-flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> {c.blockers}</div>
                              )}
                              <div className="text-[10px] text-slate-400 mt-0.5">{ago}</div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                <EvidenceSection objectiveId={o.id} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CheckInDialog({ objective, cycleId, lastProgress, onClose }) {
  const [progress, setProgress] = useState(lastProgress || 0);
  const [status, setStatus] = useState("on_track");
  const [commentary, setCommentary] = useState("");
  const [blockers, setBlockers] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/check-ins", {
        cycle_id: cycleId,
        objective_id: objective.id,
        progress_pct: Number(progress),
        status,
        commentary: commentary || null,
        blockers: blockers || null,
      });
      toast.success("Check-in logged");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent data-testid="checkin-dialog">
      <DialogHeader>
        <DialogTitle>Log a check-in</DialogTitle>
        <DialogDescription className="line-clamp-2">{objective.fields?.statement || objective.fields?.objective}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Progress: <span className="font-mono text-slate-900">{progress}%</span></Label>
          <Slider value={[progress]} onValueChange={(v) => setProgress(v[0])} min={0} max={100} step={5} className="mt-3" data-testid="checkin-progress" />
        </div>
        <div>
          <Label>Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="checkin-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="on_track">On track</SelectItem>
              <SelectItem value="at_risk">At risk</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>What's happening?</Label>
          <Textarea value={commentary} onChange={(e) => setCommentary(e.target.value)} rows={3} placeholder="What did you ship since last week? What are you focused on next?" data-testid="checkin-commentary" />
        </div>
        <div>
          <Label>Blockers (optional)</Label>
          <Textarea value={blockers} onChange={(e) => setBlockers(e.target.value)} rows={2} placeholder="What's slowing you down?" data-testid="checkin-blockers" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="checkin-submit">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Log check-in
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EvidenceSection({ objectiveId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const reload = async () => {
    const r = await api.get("/evidence", { params: { objective_id: objectiveId } });
    setItems(r.data.items || []);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [objectiveId]);

  return (
    <div className="mt-4 pt-4 border-t border-slate-100">
      <div className="flex items-center justify-between">
        <div className="axia-overline">Evidence ({items.length})</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" data-testid={`add-evidence-${objectiveId}`}><Plus className="w-3 h-3 mr-1" /> Add evidence</Button>
          </DialogTrigger>
          <EvidenceDialog objectiveId={objectiveId} onClose={() => { setOpen(false); reload(); }} />
        </Dialog>
      </div>
      {items.length > 0 && (
        <ul className="mt-2 space-y-1">
          {items.map((e) => (
            <li key={e.id} className="text-xs flex items-start gap-2 text-slate-600" data-testid={`evidence-${e.id}`}>
              {e.type === "url" ? <LinkIcon className="w-3 h-3 mt-0.5 text-slate-400" /> : <FileText className="w-3 h-3 mt-0.5 text-slate-400" />}
              <div className="flex-1 min-w-0">
                {e.type === "url" ? (
                  <a href={e.url} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-900 hover:underline">{e.title}</a>
                ) : (
                  <span className="font-medium text-slate-900">{e.title}</span>
                )}
                {e.body && <div className="text-slate-500 mt-0.5">{e.body}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EvidenceDialog({ objectiveId, onClose }) {
  const [type, setType] = useState("text");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title) return toast.error("Title required");
    if (type === "url" && !url) return toast.error("URL required");
    if (type === "file" && !file) return toast.error("Pick a file");
    setSaving(true);
    try {
      if (type === "file") {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("objective_id", objectiveId);
        fd.append("title", title);
        if (body) fd.append("body", body);
        await api.post("/evidence/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/evidence", {
          objective_id: objectiveId,
          type,
          title,
          body: body || null,
          url: type === "url" ? url : null,
        });
      }
      toast.success("Evidence added");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent data-testid="evidence-dialog">
      <DialogHeader>
        <DialogTitle>Add evidence</DialogTitle>
        <DialogDescription>Anchor your check-in with a link, note, or file.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="evidence-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Note</SelectItem>
              <SelectItem value="url">Link / URL</SelectItem>
              <SelectItem value="file">File upload</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="evidence-title" />
        </div>
        {type === "url" && (
          <div>
            <Label>URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="evidence-url" />
          </div>
        )}
        {type === "file" && (
          <div>
            <Label>File <span className="text-slate-400 font-normal">(max 25 MB · pdf, images, docs, csv)</span></Label>
            <Input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.docx,.xlsx,.pptx,.doc,.xls,.ppt,.json,.zip"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="evidence-file-input"
            />
            {file && <div className="text-[11px] text-slate-500 mt-1">{file.name} · {Math.round(file.size / 1024)} KB</div>}
          </div>
        )}
        <div>
          <Label>{type === "text" ? "Body" : "Description (optional)"}</Label>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} data-testid="evidence-body" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="evidence-submit">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Add evidence
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
